import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { api, Button, Icon } from "@tc/ui";
import { isIOS, pushState, resyncPush, turnOnPush } from "./push.js";
/**
 * "GET ALERTS ON THIS PHONE?" (Jamie, 19/10/26: "each user needs to be able to say yes or no to notifications").
 * Asked once per device after sign-in, as a small card (not a pop-up over the page):
 *  - Turn on: the browser's own question, then this device gets alerts.
 *  - Not now: not asked again on this device for 30 days; after a second Not now, never (My settings still has it).
 *  - iPhone/iPad not opened from the Home Screen: the three steps to add it, and "Done" asks again inside the Home
 *    Screen app (which keeps its own memory, so it asks there by itself).
 * Not shown when the server has no notification keys, the device already gets them, or the browser has blocked them
 * (My settings explains how to unblock). It waits for any sign-in question (trust this device, terms) to finish first.
 */
const KEY = "nozl-push-ask";
const read = () => { try {
    return { no: 0, at: 0, ...JSON.parse(localStorage.getItem(KEY) ?? "{}") };
}
catch {
    return { no: 0, at: 0 };
} };
const write = (a) => { try {
    localStorage.setItem(KEY, JSON.stringify(a));
}
catch { /* private mode: asks again next time */ } };
/** Should this device be asked now? */
export function dueToAsk(a, now = Date.now()) {
    if (a.snooze && now < a.snooze)
        return false;
    if (a.no >= 2)
        return false;
    if (a.no === 1)
        return now - a.at >= 30 * 86_400_000;
    return true;
}
export function PushPrompt() {
    const [mode, setMode] = useState(null);
    const [key, setKey] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [done, setDone] = useState(false);
    useEffect(() => {
        let stop = false;
        void (async () => {
            const st = await pushState();
            if (st === "on") {
                void resyncPush();
                return;
            }
            if (st === "blocked" || st === "unsupported" || !dueToAsk(read()))
                return;
            const p = await api("/api/me/profile").catch(() => null);
            if (!p?.pushKey)
                return; // no notification keys on this server yet
            // Let sign-in's own questions (trust this device, terms, two-factor) go first.
            for (let i = 0; i < 120 && !stop; i++) {
                await new Promise((r) => window.setTimeout(r, i ? 2000 : 2500));
                if (!document.querySelector("[aria-modal='true'], .tc-confirm, .tc-store-overlay"))
                    break;
            }
            if (stop)
                return;
            setKey(p.pushKey);
            setMode(st === "needs-home-screen" ? "ios" : "ask");
        })();
        return () => { stop = true; };
    }, []);
    if (!mode || done)
        return null;
    const notNow = () => { const a = read(); write({ no: a.no + 1, at: Date.now() }); setMode(null); };
    const turnOn = async () => {
        setBusy(true);
        setErr(null);
        try {
            await turnOnPush(key);
            setDone(true);
            write({ ...read(), no: 2, at: Date.now() });
        }
        catch (e) {
            setErr(e.message);
            if (Notification?.permission === "denied")
                write({ ...read(), no: 2, at: Date.now() });
        }
        setBusy(false);
    };
    const phone = /iPhone|Android/.test(navigator.userAgent) ? "phone" : isIOS() ? "iPad" : "device";
    return (_jsxs("aside", { className: "tc-pushcard", role: "region", "aria-label": "Notifications", children: [_jsx("span", { className: "tc-pushcard-i", "aria-hidden": "true", children: _jsx(Icon, { name: "bell", size: 20 }) }), mode === "ask" ? (_jsxs("div", { className: "tc-pushcard-b", children: [_jsxs("b", { children: ["Get alerts on this ", phone, "?"] }), _jsx("p", { children: "Price changes to do, deliveries to sign off and anything urgent, straight away. You can turn them off any time in My settings." }), err && _jsx("p", { className: "tc-pushcard-err", children: err }), _jsxs("div", { className: "tc-pushcard-a", children: [_jsx(Button, { primary: true, disabled: busy, onClick: () => void turnOn(), children: busy ? "Turning on…" : "Turn on" }), _jsx(Button, { onClick: notNow, children: "Not now" })] })] })) : (_jsxs("div", { className: "tc-pushcard-b", children: [_jsxs("b", { children: ["Get alerts on this ", phone] }), _jsx("p", { children: "On iPhone and iPad, alerts work once Nozl is on your Home Screen:" }), _jsxs("ol", { className: "tc-pushcard-steps", children: [_jsxs("li", { children: ["Tap ", _jsx("b", { children: "Share" }), " ", _jsx(Icon, { name: "upload", size: 14 }), " at the bottom of Safari"] }), _jsxs("li", { children: ["Choose ", _jsx("b", { children: "Add to Home Screen" })] }), _jsx("li", { children: "Open Nozl from the new icon: it asks you there" })] }), _jsxs("div", { className: "tc-pushcard-a", children: [_jsx(Button, { primary: true, onClick: () => { write({ ...read(), snooze: Date.now() + 86_400_000 }); setMode(null); }, children: "Done, ask me there" }), _jsx(Button, { onClick: notNow, children: "Not now" })] })] }))] }));
}
